/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import {
  Grid,
  Form,
  DateInput,
  Button,
  Icon,
  Modal,
  Tooltip,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { ChampLieu } from '@/components/Common';
import { API_DATE_FORMAT } from '@/constants';
import {
  createTimeInputHandler,
  buildEvenementDecesUpdatePayload,
  buildIndividuPutPayload,
  parseAdressePrefixee,
} from '@/utils';

interface ModalEvenementDecesProps {
  open: boolean;
  /** Identifiant de l'acte : construit l'URL de `service.update`. */
  identifiant?: number;
  /** Date et heure du décès : portées par l'ACTE. */
  acte?: any;
  /** Le LIEU du décès est porté par le DÉFUNT (`ai_dec_*`). */
  defunt?: any;
  /** Endpoint générique des individus, pour le lieu du décès. */
  urlIndividus?: string;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (acte: any) => void;
  /** Rafraîchit le défunt après l'écriture de son lieu de décès. */
  onIndividuSaved?: (individu: any) => void;
}

/**
 * Bloc « INFORMATIONS SUR LE DÉCÈS » en édition : date, heure et lieu du décès.
 *
 * Trois valeurs, deux tables :
 *
 * - date et heure sur l'acte → `PUT /numerisation-actes/deces/{id}` ;
 * - lieu sur l'individu défunt → `PUT /acte-individus/{id}` (`ad_dc_idlieu`
 *   n'est pas une clé de lieu).
 *
 * ⚠️ On écrit l'INDIVIDU d'abord, puis l'ACTE dans son `.then` — jamais en
 * parallèle (cf. `CLAUDE.md`, `request` n'est pas une `Promise`). L'ordre rend
 * aussi l'échec lisible : le premier appel a abouti, l'erreur est explicite.
 */
const CHAMPS_LIEU = ['adresseDeces'];

const ModalEvenementDeces = (props: ModalEvenementDecesProps) => {
  const {
    open,
    identifiant,
    acte,
    defunt,
    urlIndividus,
    service,
    onClose,
    onSaved,
    onIndividuSaved,
  } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      dateDeces: acte?.dateDeces,
      heureDeces: acte?.heureDeces,
      minuteDeces: acte?.minuteDeces,
      adresseDeces: parseAdressePrefixee(defunt, 'deces'),
    });
    setAttempted(false);
  }, [open, acte, defunt]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(handleChange),
    [handleChange],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      setData((prev) => ({
        ...prev,
        dateDeces: _.isNil(date) ? null : dayjs(date).format(API_DATE_FORMAT),
      }));
    },
    [],
  );

  const errors = React.useMemo(() => {
    const e: Record<string, string> = {};
    if (_.isEmpty(data?.dateDeces)) {
      e.dateDeces = 'La date de décès est obligatoire';
    }
    return e;
  }, [data]);

  const displayedErrors = attempted ? errors : {};

  /** Enregistre l'acte (date et heure), et referme. */
  const enregistrerActe = React.useCallback(() => {
    const payload = buildEvenementDecesUpdatePayload(
      identifiant as number,
      data,
    );

    const enregistrement = service?.update(payload);

    if (!enregistrement) {
      setSaving(false);
      return;
    }

    enregistrement
      .then(() => {
        onSaved({ ...acte, ...payload.acte });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de la date du décès",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [service, identifiant, acte, data, onSaved, onClose]);

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    setSaving(true);

    // Rien à écrire sur l'individu : on va droit à l'acte.
    if (!urlIndividus || _.isNil(defunt?.identifiant)) {
      enregistrerActe();
      return;
    }

    const corpsIndividu = buildIndividuPutPayload(
      defunt,
      _.pick(data, CHAMPS_LIEU),
    );

    request
      .put(`${urlIndividus}/${defunt.identifiant}`, corpsIndividu)
      .then(() => {
        onIndividuSaved?.({ ...defunt, ...corpsIndividu });
        enregistrerActe();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement du lieu du décès",
        );
        setSaving(false);
      });
  }, [errors, urlIndividus, defunt, data, onIndividuSaved, enregistrerActe]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier les informations du décès" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <Grid>
            <Grid.Row style={{ marginLeft: '1em', paddingBottom: 0 }}>
              <Grid.Column computer={6} tablet={8} mobile={16}>
                <Form.Field
                  required
                  error={!_.isNil(displayedErrors.dateDeces)}
                >
                  <label>Décédé le</label>
                  <Tooltip
                    trigger={
                      <DateInput
                        value={
                          data?.dateDeces
                            ? dayjs(data.dateDeces).toDate()
                            : undefined
                        }
                        onChange={handleDateChange}
                      />
                    }
                    type={displayedErrors.dateDeces ? 'error' : ''}
                    message={displayedErrors.dateDeces}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={5} tablet={8} mobile={16}>
                <Form.Group style={{ alignItems: 'flex-end' }}>
                  <Form.Input
                    label="à"
                    placeholder="hh"
                    name="heureDeces"
                    style={{ width: 48 }}
                    value={
                      !_.isNil(data?.heureDeces)
                        ? String(data.heureDeces).padStart(2, '0')
                        : ''
                    }
                    onChange={handleTimeInput}
                  />
                  <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>
                    :
                  </span>
                  <Form.Input
                    placeholder="mm"
                    name="minuteDeces"
                    style={{ width: 48 }}
                    value={
                      !_.isNil(data?.minuteDeces)
                        ? String(data.minuteDeces).padStart(2, '0')
                        : ''
                    }
                    onChange={handleTimeInput}
                  />
                </Form.Group>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <ChampLieu
                  data={data}
                  name="adresseDeces"
                  title="Lieu de décès"
                  onChange={handleChange}
                  dateValidite={
                    data?.dateDeces
                      ? dayjs(data.dateDeces).format(API_DATE_FORMAT)
                      : undefined
                  }
                  required={false}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="evenement-deces-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEvenementDeces;
